import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, Badge } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const BodegaScreen = () => {
  const navigation = useNavigation();
  const [pendientes, setPendientes] = useState([]);
  const [preparados, setPreparados] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('pendientes');

  const loadData = useCallback(async () => {
    try {
      // Solo spinner full-screen en la primera carga. En recargas
      // posteriores (focus al volver de un detalle) el FlatList sigue
      // montado y se preserva el scroll del usuario.
      if (pendientes.length === 0 && preparados.length === 0) {
        setLoading(true);
      }
      const [pend, prep, res] = await Promise.all([
        api.getBodegaPendientes(),
        api.getBodegaPreparados(),
        api.getBodegaResumen(),
      ]);
      setPendientes(pend);
      setPreparados(prep);
      setResumen(res);
    } catch (error) {
      console.error('Error al cargar datos de bodega:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pendientes.length, preparados.length]);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const formatFecha = (fecha) => {
    const date = new Date(fecha);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return `Hoy ${date.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('es-HN', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const renderResumen = () => (
    <View style={styles.resumenContainer}>
      <View style={[styles.resumenCard, { backgroundColor: '#FFF3CD' }]}>
        <Ionicons name="time" size={24} color="#856404" />
        <Text style={[styles.resumenNumero, { color: '#856404' }]}>{resumen.pendientes || 0}</Text>
        <Text style={styles.resumenLabel}>Pendientes</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#D1ECF1' }]}>
        <Ionicons name="checkmark-done" size={24} color="#0C5460" />
        <Text style={[styles.resumenNumero, { color: '#0C5460' }]}>{resumen.preparados_hoy || 0}</Text>
        <Text style={styles.resumenLabel}>Preparados Hoy</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#D4EDDA' }]}>
        <Ionicons name="car" size={24} color="#155724" />
        <Text style={[styles.resumenNumero, { color: '#155724' }]}>{resumen.en_camino_hoy || 0}</Text>
        <Text style={styles.resumenLabel}>En Camino</Text>
      </View>
    </View>
  );

  const renderPedidoItem = ({ item }) => (
    <TouchableOpacity
      onPress={() => navigation.navigate('BodegaChecklist', { pedido: item })}
    >
      <Card style={styles.pedidoCard}>
        <Card.Content>
          <View style={styles.pedidoHeader}>
            <View style={styles.pedidoInfo}>
              <View style={styles.pedidoIdRow}>
                <Text style={styles.pedidoId}>Pedido #{item.id}</Text>
                {tab === 'pendientes' && (
                  <Badge style={styles.urgentBadge}>VERIFICAR</Badge>
                )}
                {tab === 'preparados' && (
                  <View style={[styles.estadoChip, { backgroundColor: '#17A2B8' }]}>
                    <Ionicons name="checkmark-done" size={12} color="#fff" />
                    <Text style={styles.estadoText}> LISTO</Text>
                  </View>
                )}
              </View>
              <Text style={styles.pedidoCliente}>
                <Ionicons name="person" size={14} color="#666" /> {item.cliente_nombre || 'Sin cliente'}
              </Text>
              <Text style={styles.pedidoVendedor}>
                <Ionicons name="business" size={14} color="#888" /> Vendedor: {item.vendedor_nombre || 'N/A'}
              </Text>
            </View>
            <View style={styles.pedidoRight}>
              <Text style={styles.pedidoTotal}>L. {parseFloat(item.total).toFixed(2)}</Text>
              <Text style={styles.pedidoItems}>
                {parseInt(item.total_productos || item.total_items || 0)} items
              </Text>
            </View>
          </View>

          <Divider style={styles.divider} />

          <View style={styles.pedidoFooter}>
            <Text style={styles.pedidoFecha}>
              <Ionicons name="calendar" size={12} color="#888" /> {formatFecha(item.fecha)}
            </Text>
            {tab === 'pendientes' && (
              <Text style={styles.tapText}>
                Toca para verificar <Ionicons name="arrow-forward" size={12} color="#007bff" />
              </Text>
            )}
          </View>
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#17A2B8" />
        <Text style={styles.loadingText}>Cargando panel de bodega...</Text>
      </View>
    );
  }

  const dataToShow = tab === 'pendientes' ? pendientes : preparados;

  return (
    <View style={styles.container}>
      {renderResumen()}

      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, tab === 'pendientes' && styles.tabActive]}
          onPress={() => setTab('pendientes')}
        >
          <Ionicons name="time" size={18} color={tab === 'pendientes' ? '#FFC107' : '#999'} />
          <Text style={[styles.tabText, tab === 'pendientes' && styles.tabTextActive]}>
            Pendientes ({pendientes.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, tab === 'preparados' && styles.tabActive]}
          onPress={() => setTab('preparados')}
        >
          <Ionicons name="checkmark-done" size={18} color={tab === 'preparados' ? '#17A2B8' : '#999'} />
          <Text style={[styles.tabText, tab === 'preparados' && styles.tabTextActive]}>
            Preparados Hoy ({preparados.length})
          </Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={dataToShow}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderPedidoItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name={tab === 'pendientes' ? 'checkmark-circle' : 'cube-outline'} size={64} color="#ccc" />
            <Text style={styles.emptyText}>
              {tab === 'pendientes' ? 'No hay pedidos pendientes' : 'No hay pedidos preparados hoy'}
            </Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  resumenContainer: { flexDirection: 'row', padding: 8, justifyContent: 'space-between' },
  resumenCard: { flex: 1, alignItems: 'center', padding: 12, borderRadius: 8, marginHorizontal: 4, elevation: 1 },
  resumenNumero: { fontSize: 24, fontWeight: 'bold', marginTop: 4 },
  resumenLabel: { fontSize: 10, color: '#666', marginTop: 2, textAlign: 'center' },
  tabsContainer: { flexDirection: 'row', paddingHorizontal: 8, marginBottom: 8 },
  tab: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabActive: { borderBottomColor: '#17A2B8' },
  tabText: { fontSize: 13, color: '#999', marginLeft: 4 },
  tabTextActive: { color: '#333', fontWeight: 'bold' },
  listContainer: { padding: 8 },
  pedidoCard: { elevation: 2 },
  pedidoHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  pedidoInfo: { flex: 1, marginRight: 10 },
  pedidoIdRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  pedidoId: { fontSize: 16, fontWeight: 'bold', color: '#333', marginRight: 8 },
  urgentBadge: { backgroundColor: '#FFC107', color: '#000', fontSize: 10 },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 },
  estadoText: { fontSize: 10, fontWeight: 'bold', color: '#fff' },
  pedidoCliente: { fontSize: 13, color: '#666', marginBottom: 2 },
  pedidoVendedor: { fontSize: 12, color: '#888' },
  pedidoRight: { alignItems: 'flex-end' },
  pedidoTotal: { fontSize: 16, fontWeight: 'bold', color: '#28a745' },
  pedidoItems: { fontSize: 12, color: '#888', marginTop: 2 },
  divider: { marginVertical: 8 },
  pedidoFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  pedidoFecha: { fontSize: 12, color: '#888' },
  tapText: { fontSize: 12, color: '#007bff', fontWeight: '500' },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, color: '#888', fontSize: 16 },
});

export default BodegaScreen;
